import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import Overview from './components/Overview';
import ServicesManagement from './components/ServicesManagement';
import ProvidersManagement from './components/ProvidersManagement';
import ClientsManagement from './components/ClientsManagement';
import JobsManagement from './components/JobsManagement';
import PaymentsManagement from './components/PaymentsManagement';
import ProviderPayoutsManagement from './components/ProviderPayoutsManagement';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import MessagingPanel from './components/MessagingPanel';
import NotificationsPanel from './components/NotificationsPanel';
import DiscountCodesManagement from './components/DiscountCodesManagement';
import AdminSupportPanel from './components/AdminSupportPanel';
import InvoicesManagement from './components/InvoicesManagement';
import RefundsManagement from './components/RefundsManagement';
import VanFleetManagement from './components/VanFleetManagement';
import TransportVerificationManagement from './components/TransportVerificationManagement';
import WasteVerificationManagement from './components/WasteVerificationManagement';
import AdminManagement from './components/AdminManagement';
import WorkwearManagement from './components/WorkwearManagement';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [admin, setAdmin] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [unreadSupport, setUnreadSupport] = useState(0);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
    { id: 'providers', label: 'Providers', icon: 'ri-user-star-line' },
    { id: 'clients', label: 'Clients', icon: 'ri-user-line' },
    { id: 'jobs', label: 'Jobs', icon: 'ri-briefcase-line' },
    { id: 'services', label: 'Services', icon: 'ri-tools-line' },
    { id: 'payments', label: 'Payments', icon: 'ri-money-pound-circle-line' },
    { id: 'invoices', label: 'Invoices', icon: 'ri-file-list-3-line' },
    { id: 'discounts', label: 'Discount Codes', icon: 'ri-coupon-line' },
    { id: 'van-fleet', label: 'Van Fleet', icon: 'ri-truck-line' },
    { id: 'analytics', label: 'Analytics', icon: 'ri-bar-chart-line' },
    { id: 'messaging', label: 'Messaging', icon: 'ri-message-3-line' },
    { id: 'notifications', label: 'Notifications', icon: 'ri-notification-3-line' },
    { id: 'support', label: 'Support', icon: 'ri-customer-service-2-line' },
  ];

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
    { id: 'jobs', label: 'Jobs', icon: 'ri-briefcase-line' },
    { id: 'providers', label: 'Providers', icon: 'ri-user-settings-line' },
    { id: 'clients', label: 'Clients', icon: 'ri-user-line' },
    { id: 'services', label: 'Services', icon: 'ri-tools-line' },
    { id: 'payments', label: 'Payments', icon: 'ri-money-pound-circle-line' },
    { id: 'payouts', label: 'Provider Payouts', icon: 'ri-bank-line' },
    { id: 'invoices', label: 'Invoices', icon: 'ri-file-list-3-line' },
    { id: 'refunds', label: 'Refunds', icon: 'ri-refund-2-line' },
    { id: 'discounts', label: 'Discount Codes', icon: 'ri-coupon-line' },
    { id: 'workwear', label: 'Workwear Store', icon: 'ri-shirt-line' },
    { id: 'transport-verification', label: 'Transport Verification', icon: 'ri-truck-line' },
    { id: 'waste-verification', label: 'Waste Verification', icon: 'ri-recycle-line' },
    { id: 'van-fleet', label: 'Van Fleet', icon: 'ri-car-line' },
    { id: 'analytics', label: 'Analytics', icon: 'ri-bar-chart-line' },
    { id: 'messaging', label: 'Messaging', icon: 'ri-message-3-line' },
    { id: 'notifications', label: 'Notifications', icon: 'ri-notification-3-line' },
    { id: 'support', label: 'Support', icon: 'ri-customer-service-2-line' },
  ];

  useEffect(() => {
    // Set document title and meta tags
    document.title = 'Admin Dashboard | Platform Management - Taskbudy';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Taskbudy administrator dashboard for platform management, user oversight, and system analytics.');
    }
    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', 'noindex, nofollow');
    }
  }, []);

  useEffect(() => {
    loadAdminData();
  }, []);

  useEffect(() => {
    if (admin) {
      loadUnreadSupportCount();
      
      // Subscribe to new support messages
      const channel = supabase
        .channel('admin_support_messages')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'admin_messages',
            filter: 'recipient_type=eq.admin'
          },
          () => {
            loadUnreadSupportCount();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [admin]);

  const loadUnreadSupportCount = async () => {
    const { count } = await supabase
      .from('admin_messages')
      .select('*', { count: 'exact', head: true })
      .eq('recipient_type', 'admin')
      .eq('is_read', false);

    setUnreadSupport(count || 0);
  };

  const loadAdminData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/auth/signin');
        return;
      }

      const { data: adminData } = await supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (!adminData) {
        navigate('/');
        return;
      }

      setAdmin(adminData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading admin data:', error);
      navigate('/auth/signin');
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
    } catch (err: any) {
      console.error('Sign out error:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
      </div>
    );
  }

  const renderContent = () => {
    if (activeTab === 'transport-verification') {
      return <TransportVerificationManagement />;
    }

    switch (activeTab) {
      case 'overview':
        return <Overview admin={admin} onNavigate={setActiveTab} />;
      case 'providers':
        return <ProvidersManagement admin={admin} />;
      case 'clients':
        return <ClientsManagement admin={admin} />;
      case 'jobs':
        return <JobsManagement />;
      case 'services':
        return <ServicesManagement admin={admin} />;
      case 'payments':
        return <PaymentsManagement />;
      case 'payouts':
        return <ProviderPayoutsManagement />;
      case 'refunds':
        return <RefundsManagement />;
      case 'invoices':
        return <InvoicesManagement />;
      case 'discounts':
        return <DiscountCodesManagement />;
      case 'van-fleet':
        return <VanFleetManagement />;
      case 'analytics':
        return <AnalyticsDashboard />;
      case 'messaging':
      case 'messages':
        return <MessagingPanel />;
      case 'notifications':
        return <NotificationsPanel />;
      case 'support':
        return <AdminSupportPanel admin={admin} onUnreadChange={setUnreadSupport} />;
      case 'admin-management':
        return <AdminManagement currentAdmin={admin} />;
      case 'workwear':
        return <WorkwearManagement />;
      default:
        return <Overview admin={admin} onNavigate={setActiveTab} />;
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 overflow-x-hidden">
        {/* Top Navigation */}
        <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 animate-[slideDown_0.5s_ease-out]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-2 sm:gap-4">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-all duration-300 cursor-pointer transform hover:scale-110"
                  aria-label="Toggle sidebar"
                >
                  <i className="ri-menu-line text-2xl"></i>
                </button>
                <div 
                  className="flex items-center space-x-2 cursor-pointer transform hover:scale-105 transition-transform duration-300"
                  onClick={() => navigate('/')}
                >
                  <div className="bg-[#0B3C5D] px-2 sm:px-4 py-2 rounded-lg">
                    <img 
                      src="https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/dc77c2be5a78759d16f1acf02198b697.png" 
                      alt="TaskBudy" 
                      className="h-8 md:h-10"
                    />
                  </div>
                </div>
                <span className="hidden sm:inline-block text-sm font-semibold text-gray-700">Admin Panel</span>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-all duration-300 cursor-pointer transform hover:scale-110"
                aria-label="Toggle menu"
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-more-2-fill'} text-2xl transition-transform duration-300 ${mobileMenuOpen ? 'rotate-90' : ''}`}></i>
              </button>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center gap-4">
                <button 
                  onClick={() => setActiveTab('support')}
                  className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-all duration-300 cursor-pointer relative transform hover:scale-110"
                >
                  <i className="ri-customer-service-2-line text-xl"></i>
                  {unreadSupport > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                      {unreadSupport}
                    </span>
                  )}
                </button>
                <button 
                  onClick={() => setActiveTab('notifications')}
                  className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-all duration-300 cursor-pointer relative transform hover:scale-110"
                >
                  <i className="ri-notification-3-line text-xl"></i>
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                </button>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer font-medium transform hover:scale-105"
                >
                  Logout
                </button>
              </div>
            </div>

            {/* Mobile Menu Dropdown */}
            {mobileMenuOpen && (
              <div className="lg:hidden border-t border-gray-200 py-4 animate-[fadeIn_0.3s_ease-out]">
                <div className="flex flex-col gap-3">
                  <button 
                    onClick={() => {
                      setActiveTab('support');
                      setMobileMenuOpen(false);
                    }}
                    className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-medium transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg text-left flex items-center gap-3 transform hover:translate-x-2"
                  >
                    <i className="ri-customer-service-2-line text-xl"></i>
                    <span>Live Support</span>
                    {unreadSupport > 0 && (
                      <span className="ml-auto w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                        {unreadSupport}
                      </span>
                    )}
                  </button>
                  <button 
                    onClick={() => {
                      setActiveTab('notifications');
                      setMobileMenuOpen(false);
                    }}
                    className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-medium transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg text-left flex items-center gap-3 transform hover:translate-x-2"
                  >
                    <i className="ri-notification-3-line text-xl"></i>
                    <span>Notifications</span>
                    <span className="ml-auto w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                  </button>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="px-4 py-3 text-red-600 hover:bg-red-50 font-medium transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg text-left flex items-center gap-3 transform hover:translate-x-2"
                  >
                    <i className="ri-logout-box-line text-xl"></i>
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="flex">
          {/* Sidebar Navigation */}
          <aside className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:sticky top-16 left-0 w-64 bg-white border-r border-gray-200 h-[calc(100vh-4rem)] transition-transform duration-300 z-40 overflow-y-auto`}>
            <nav className="p-4 space-y-2">
              <button
                onClick={() => {
                  setActiveTab('overview');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'overview'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-dashboard-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Overview</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('providers');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'providers'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-team-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Providers</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('clients');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'clients'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-user-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Clients</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('jobs');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'jobs'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-briefcase-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Jobs</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('services');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'services'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-tools-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Services</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('payments');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'payments'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-money-pound-circle-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Payments</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('payouts');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'payouts'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-bank-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Provider Payouts</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('invoices');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'invoices'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-file-list-3-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Invoices</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('discounts');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'discounts'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-coupon-3-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Discount Codes</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('workwear');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'workwear'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-shirt-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Workwear Store</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('van-fleet');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'van-fleet'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-truck-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Van Fleet</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('notifications');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'notifications'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-notification-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Notifications</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('messaging');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'messaging' || activeTab === 'messages'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-message-3-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Messages</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('support');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'support'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-customer-service-2-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Live Support</span>
                {unreadSupport > 0 && (
                  <span className="ml-auto w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                    {unreadSupport}
                  </span>
                )}
              </button>

              <button
                onClick={() => {
                  setActiveTab('analytics');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'analytics'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-bar-chart-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Analytics</span>
              </button>

              {admin?.is_super_admin && (
                <button
                  onClick={() => {
                    setActiveTab('admin-management');
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === 'admin-management'
                      ? 'bg-amber-50 text-amber-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <i className="ri-shield-user-line text-xl w-6 h-6 flex items-center justify-center"></i>
                  <span className="font-medium">Admin Management</span>
                </button>
              )}

              <button
                onClick={() => setActiveTab('transport-verification')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'transport-verification'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-truck-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Transport Verification</span>
              </button>

              <button
                onClick={async () => {
                  try {
                    await supabase.auth.signOut();
                    navigate('/');
                  } catch (err: any) {
                    console.error('Sign out error:', err);
                  }
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-logout-box-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium">Sign Out</span>
              </button>
            </nav>
          </aside>

          {/* Overlay for mobile */}
          {sidebarOpen && (
            <div 
              className="fixed inset-0 bg-black/50 z-30 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            ></div>
          )}

          {/* Main Content */}
          <main className="flex-1 p-4 sm:p-6 w-full lg:w-[calc(100%-16rem)]">
            {renderContent()}
          </main>
        </div>
      </div>
    </>
  );
}

export default AdminDashboard;
